import api from './api';

export type MessageSender = 'customer' | 'team';

export interface ThreadMessage {
  id: string;
  sender: MessageSender;
  body: string;
  createdAt: string;
  readAt: string | null;
  deliveredAt: string | null;
  deliveryError: string | null;
}

export interface MessageThread {
  id: string;
  websiteId: string | null;
  lastMessageAt: string;
  messages: ThreadMessage[];
  unreadCount: number;
}

export interface AdminThreadSummary {
  id: string;
  user: { id: string; name: string; email: string };
  businessName: string | null;
  websiteDomain: string | null;
  lastMessageAt: string;
  lastMessage: { sender: MessageSender; body: string; createdAt: string } | null;
  unreadCount: number;
  awaitingReply: boolean;
  deliveryError: string | null;
}

export interface AdminThreadDetail extends AdminThreadSummary {
  messages: ThreadMessage[];
}

/** Customer side — the `/welcome` panel. */
export const messageService = {
  getThread: async (): Promise<MessageThread> => {
    const response = await api.get('/api/messages/thread');
    return response.data.data as MessageThread;
  },

  send: async (body: string): Promise<ThreadMessage> => {
    const response = await api.post('/api/messages', { body });
    return response.data.data as ThreadMessage;
  },

  markRead: async (): Promise<void> => {
    await api.post('/api/messages/read');
  },
};

/** Owner side — the admin inbox. */
export const adminMessageService = {
  listThreads: async (): Promise<{
    threads: AdminThreadSummary[];
    total: number;
    unreadTotal: number;
  }> => {
    const response = await api.get('/api/admin/messages');
    return response.data.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/api/admin/messages/unread-count');
    return response.data.data.count as number;
  },

  getThread: async (threadId: string): Promise<AdminThreadDetail> => {
    const response = await api.get(`/api/admin/messages/${threadId}`);
    return response.data.data as AdminThreadDetail;
  },

  markRead: async (threadId: string): Promise<void> => {
    await api.post(`/api/admin/messages/${threadId}/read`);
  },

  reply: async (threadId: string, body: string): Promise<ThreadMessage> => {
    const response = await api.post(`/api/admin/messages/${threadId}/reply`, { body });
    return response.data.data as ThreadMessage;
  },
};
